import { z } from 'zod';

import * as api from '@gitbook/api-client';

export const NavigationSiteInsightsReportsValues = [
    'traffic',
    'search',
    'ask',
    'links',
    'openapi',
    'pages',
    'ads',
    'urls',
    'mcp',
    'agents',
    'assistant',
    'engagement',
] as const;

export const NavigationSiteSettingsSectionsValues = [
    'general',
    'audience',
    'schema',
    'domains',
    'redirects',
    'mcp',
    'agent',
    'embed',
    'sponsorship',
    'legal',
    'plan',
    'connections',
    'channels',
] as const;

export const NavigationSiteCustomizationTabsValues = [
    'general',
    'layout',
    'configure',
    'ai',
] as const;

/**
 * Screen listing integrations
 */
export enum NavigationIntegrationsProvider {
    Marketplace = 'marketplace',
    Organization = 'organization',
}

export enum NavigationBillingQueryParams {
    UpgradeSuccess = 'upgrade_success',
}

const typedUnknown = <T>() => z.unknown() as z.ZodType<T>;

export const BaseNavigationScreenSchema = z
    .object({
        hash: z.string().optional(),
        query: z.string().optional(),
        askAI: z.boolean().optional(),
        coupon: z.string().optional(),
        ssoLogin: z
            .object({
                noSSO: z.literal('account_exists').optional(),
                email: z.string().optional(),
            })
            .strict()
            .optional(),
    })
    .strict();

export const NavigationScreenChangeRequestsFiltersSchema = z
    .object({
        status: z.array(z.enum(api.ChangeRequestStatus)).optional(),
        contributor: z.string().optional(),
        requestedReview: z.string().optional(),
        creator: z.string().optional(),
        space: z.string().optional(),
        finding: z.enum(['none', 'all']).optional(),
    })
    .strict();

/** Filters supported by the space-scoped change requests side sheet. */
export const NavigationChangeRequestsSideSheetFiltersSchema =
    NavigationScreenChangeRequestsFiltersSchema.omit({ space: true });

/**
 * Relative activity-window presets for the findings list, matching findings by
 * when they were last updated. Relative (not absolute dates) so a shared or
 * reloaded link keeps meaning "active in the last N days". `all` is the
 * explicit "any activity" escape from the `last_7_days` default; it is a real
 * value (not the absence of the field) so it can override that default.
 */
export const NavigationScreenSiteFindingsDateRangeValues = [
    'all',
    'last_7_days',
    'last_30_days',
] as const;

export const NavigationScreenSiteFindingsFiltersSchema = z
    .object({
        topic: z.string().optional(),
        status: z.enum(api.SiteFindingStatus).optional(),
        type: z.enum(api.SiteFindingType).optional(),
        severity: z.enum(api.SiteFindingSeverity).optional(),
        source: z.array(z.string()).optional(),
        hasChangeRequests: z.boolean().optional(),
        dateRange: z.enum(NavigationScreenSiteFindingsDateRangeValues).optional(),
        /** Where a link finding stands; see the API's `SiteFindingState`. */
        state: z.enum(api.SiteFindingState).optional(),
    })
    .strict();

export const NavigationScreenSiteScansFiltersSchema = z
    .object({
        topic: z.string().optional(),
        status: z.enum(api.SiteScanStatus).optional(),
    })
    .strict();

export const NavigationChangeRequestsSideSheetSchema = z
    .object({
        type: z.literal('change-requests'),
        tab: z.undefined().optional(),
        filters: NavigationChangeRequestsSideSheetFiltersSchema.optional(),
    })
    .strict();

export const NavigationGitSyncSideSheetSchema = z
    .object({
        type: z.literal('gitsync'),
        tab: z.enum(['setup', 'status']).optional(),
    })
    .strict();

export const NavigationShareSideSheetSchema = z
    .object({
        type: z.literal('share'),
        tab: z.enum(['invite', 'docs-sites']).optional(),
    })
    .strict();

export const NavigationMergeRulesSideSheetSchema = z
    .object({
        type: z.literal('merge-rule'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationMergeStatusSideSheetSchema = z
    .object({
        type: z.literal('merge-status'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationCommentsSideSheetSchema = z
    .object({
        type: z.literal('comments'),
        tab: z.undefined().optional(),
        node: z.string().optional(),
        context: z.literal('preview').optional(),
        comment: z.string().optional(),
        tableCell: (
            z.object({
                record: z.string(),
                definition: z.string(),
            }) satisfies z.ZodType<api.TableCellTarget>
        ).optional(),
    })
    .strict();

export const NavigationChangeRequestReviewsSideSheetSchema = z
    .object({
        type: z.literal('reviews'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationPageOptionsSideSheetSchema = z
    .object({
        type: z.literal('page'),
        tab: z.enum(['layout', 'visibility', 'metadata']).optional(),
    })
    .strict();

export const NavigationAudienceSideSheetSchema = z
    .object({
        type: z.literal('audience'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationIntegrationsModalViewSchema = z.union([
    z
        .object({
            type: z.literal('installations'),
        })
        .strict(),
    z
        .object({
            type: z.literal('category'),
            category: z.enum(api.IntegrationCategory).optional(),
            provider: z.enum(NavigationIntegrationsProvider).optional(),
            search: z.string().optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal('integration'),
            integration: z.string(),
            state: z.literal('configure').optional(),
        })
        .strict(),
]);

export const NavigationIntegrationsModalSchema = z
    .object({
        type: z.literal('integrations'),
        tab: z.undefined().optional(),
        view: NavigationIntegrationsModalViewSchema.optional(),
    })
    .strict();

export const NavigationPublishedSideSheetSchema = z
    .object({
        type: z.literal('published'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationPublishSideSheetSchema = z
    .object({
        type: z.literal('publish'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationImportsSideSheetSchema = z
    .object({
        type: z.literal('imports'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationHistorySideSheetSchema = z
    .object({
        type: z.literal('history'),
        tab: z.undefined().optional(),
    })
    .strict();

export const NavigationSpaceSideSheetSchema = z.union([
    NavigationChangeRequestsSideSheetSchema,
    NavigationGitSyncSideSheetSchema,
    NavigationShareSideSheetSchema,
    NavigationCommentsSideSheetSchema,
    NavigationPageOptionsSideSheetSchema,
    NavigationIntegrationsModalSchema,
    NavigationMergeRulesSideSheetSchema,
    NavigationImportsSideSheetSchema,
    NavigationHistorySideSheetSchema,
]);

export const NavigationChangeRequestSideSheetSchema = z.union([
    NavigationCommentsSideSheetSchema,
    NavigationPageOptionsSideSheetSchema,
    NavigationShareSideSheetSchema,
    NavigationIntegrationsModalSchema,
    NavigationMergeRulesSideSheetSchema,
    NavigationMergeStatusSideSheetSchema,
    NavigationImportsSideSheetSchema,
    NavigationHistorySideSheetSchema,
]);

export const NavigationCollectionSideSheetSchema = NavigationShareSideSheetSchema;

export const NavigationSiteSideSheetSchema = z.union([
    NavigationIntegrationsModalSchema,
    NavigationAudienceSideSheetSchema,
    NavigationPublishedSideSheetSchema,
    NavigationPublishSideSheetSchema,
    NavigationGitSyncSideSheetSchema,
]);

export const NavigationScreenAnalyticsFiltersSchema = z
    .object({
        range: typedUnknown<api.SiteInsightsQueryEventsAggregation['range']>().optional(),
        // Bucket size the reader picked, kept apart from the range so it can override what it implies.
        interval: typedUnknown<api.SiteInsightsQueryDateTimeColumn['interval']>().optional(),
        // Baseline the figures are compared against; the app narrows it to the ones it knows.
        compare: z.string().optional(),
        where: typedUnknown<api.SiteInsightsQueryEventsAggregation['where']>().optional(),
        groupBy: typedUnknown<api.SiteInsightsQueryEventsAggregation['groupBy']>().optional(),
        // Not a `where`: it selects the population a report counts, which the reader can
        // still narrow with a filter on the same column.
        audience: z.array(z.string()).optional(),
        // The Assistant report filters site questions, which the events columns above
        // cannot express.
        relevance: typedUnknown<api.SiteQuestionAnswerRelevance[]>().optional(),
        answered: typedUnknown<api.SiteQuestionAnswerResolution[]>().optional(),
        channelType: typedUnknown<api.SiteChannelType[]>().optional(),
        topic: z.array(z.string()).optional(),
    })
    .strict();

export const NavigationScreenSiteTabAnalyticsSchema = z
    .object({
        type: z.literal('analytics'),
        report: z.enum(NavigationSiteInsightsReportsValues),
        /** Question the Assistant report is drilled into, and the conversation shown beside it. */
        question: z.string().optional(),
        answer: z.string().optional(),
        /** Topic the Assistant report is drilled into. */
        topic: z.string().optional(),
        filters: NavigationScreenAnalyticsFiltersSchema.optional(),
    })
    .strict()
    // The tab is one detail at a time. Both set would silently drop the topic, since every
    // consumer reads `question` first.
    .refine((tab) => !(tab.question && tab.topic), {
        message: 'An analytics tab cannot be drilled into a question and a topic at once',
        path: ['topic'],
    });

export const NavigationScreenSiteTabSettingsSchema = z
    .object({
        type: z.literal('settings'),
        section: z.enum(NavigationSiteSettingsSectionsValues).optional(),
    })
    .strict();

export const NavigationScreenSiteTabPreviewSchema = z
    .object({
        type: z.literal('preview'),
    })
    .strict();

export const NavigationScreenSiteTabStructureSchema = z
    .object({
        type: z.literal('structure'),
        selected: z.string().optional(),
    })
    .strict();

export const NavigationScreenSiteTabMembersSchema = z
    .object({
        type: z.literal('members'),
    })
    .strict();

export const NavigationScreenSiteTabCustomizationSchema = z
    .object({
        type: z.literal('customization'),
        siteSpaceId: z.string().optional(),
        tab: z.enum(NavigationSiteCustomizationTabsValues),
    })
    .strict();

/** Used internally by triage mode; not a routed tab. */
export const NavigationScreenSiteTabFindingsSchema = z
    .object({
        type: z.literal('findings'),
        finding: z.string().optional(),
        filters: NavigationScreenSiteFindingsFiltersSchema.optional(),
    })
    .strict();

export const NavigationScreenSiteTabTriageSchema = z
    .object({
        type: z.literal('triage'),
        view: z.union([z.literal('settings'), z.literal('scans')]).optional(),
        finding: z.string().optional(),
        filters: z
            .object({
                topic: z.string().optional(),
                status: z.enum(api.SiteFindingStatus).optional(),
                severity: z.enum(api.SiteFindingSeverity).optional(),
            })
            .strict()
            .optional(),
        scan: z.string().optional(),
        scanFilters: NavigationScreenSiteScansFiltersSchema.optional(),
    })
    .strict();

export const NavigationScreenSiteTabSchema = z.union([
    NavigationScreenSiteTabAnalyticsSchema,
    NavigationScreenSiteTabSettingsSchema,
    NavigationScreenSiteTabPreviewSchema,
    NavigationScreenSiteTabStructureSchema,
    NavigationScreenSiteTabMembersSchema,
    NavigationScreenSiteTabCustomizationSchema,
]);

const createScreenSchema = <Shape extends z.ZodRawShape>(shape: Shape) =>
    BaseNavigationScreenSchema.extend(shape).strict();

export const NavigationScreenErrorNotFoundSchema = createScreenSchema({
    type: z.literal('error_notfound'),
});

export const NavigationScreenJoinSchema = createScreenSchema({
    type: z.literal('join'),
});

export const NavigationScreenRootSchema = createScreenSchema({
    type: z.literal('root'),
    organization: z.string().optional(),
});

export const NavigationScreenHomeStateSchema = createScreenSchema({
    type: z.literal('home'),
    organization: z.string().optional(),
});

export const NavigationScreenOAuthAuthorizeSchema = createScreenSchema({
    type: z.literal('oauth_authorize'),
    sessionId: z.string().optional(),
    /** Show the sign up form (instead of sign in) while unauthenticated. */
    signup: z.boolean().optional(),
});

export const NavigationScreenUserStateSchema = createScreenSchema({
    type: z.literal('user'),
    user: z.string(),
});

export const NavigationScreenWelcomeSchema = createScreenSchema({
    type: z.literal('welcome'),
    organizationType: z.enum(['business', 'community']).optional(),
});

export const NavigationRevisionCurrent = Symbol('CURRENT_REVISION');

const NavigationRevisionCurrentSchema = z.custom<typeof NavigationRevisionCurrent>(
    (value) => value === NavigationRevisionCurrent
);

export const NavigationRevisionSchema = z.union([z.string(), NavigationRevisionCurrentSchema]);

const NavigationScreenSpaceBaseStateSchema = createScreenSchema({
    type: z.literal('space'),
    space: z.string(),
    site: z.string().optional(),
    organization: z.string().optional(),
    page: z.string(),
    mode: z.enum(['edit', 'preview', 'diff', 'overview', 'checks']).optional(),
    revision: NavigationRevisionSchema.optional(),
    baseRevision: z.string().optional(),
    reusableContent: z.string().optional(),
});

export const NavigationScreenSpaceRevisionStateSchema = NavigationScreenSpaceBaseStateSchema.extend(
    {
        changeRequest: z.undefined().optional(),
        sideSheet: NavigationSpaceSideSheetSchema.optional(),
    }
).strict();

export const NavigationScreenSpaceChangeRequestStateSchema =
    NavigationScreenSpaceBaseStateSchema.extend({
        changeRequest: z.union([z.string(), z.number()]),
        sideSheet: NavigationChangeRequestSideSheetSchema.optional(),
        /** Key of the change selected in the overview's changed-pages sidebar. */
        change: z.string().optional(),
    }).strict();

export const NavigationScreenSpaceStateSchema = z.union([
    NavigationScreenSpaceRevisionStateSchema,
    NavigationScreenSpaceChangeRequestStateSchema,
]);

export const NavigationScreenCollectionStateSchema = createScreenSchema({
    type: z.literal('collection'),
    collection: z.string(),
    organization: z.string().optional(),
    sideSheet: NavigationCollectionSideSheetSchema.optional(),
});

export const NavigationScreenSiteStateSchema = createScreenSchema({
    type: z.literal('site'),
    site: z.string(),
    organization: z.string().optional(),
    tab: NavigationScreenSiteTabSchema.optional(),
    sideSheet: NavigationSiteSideSheetSchema.optional(),
    billing: z.enum(NavigationBillingQueryParams).optional(),
});

export const NavigationScreenSiteStructureStateSchema = createScreenSchema({
    type: z.literal('site'),
    site: z.string(),
    organization: z.string(),
    tab: NavigationScreenSiteTabStructureSchema,
    sideSheet: NavigationSiteSideSheetSchema.optional(),
    billing: z.enum(NavigationBillingQueryParams).optional(),
});

export const NavigationScreenSitesListStateSchema = createScreenSchema({
    type: z.literal('sites'),
    organization: z.string().optional(),
    status: z.enum(['published', 'unpublished']).optional(),
    search: z.string().optional(),
});

export const NavigationScreenSitePreviewAuthSchema = createScreenSchema({
    type: z.literal('site_preview_auth'),
    organization: z.string(),
    site: z.string(),
    redirect: z.string().optional(),
});

export const NavigationScreenOrgInviteSchema = createScreenSchema({
    type: z.literal('org_invite'),
    organization: z.string(),
});

export const NavigationScreenSiteInviteSchema = createScreenSchema({
    type: z.literal('site_invite'),
    organization: z.string(),
    site: z.string(),
});

export const NavigationScreenOrgWithBaseNameSchema = createScreenSchema({
    type: z.literal('org_with_basename'),
    organizationBaseName: z.string(),
});

export const NavigationScreenOrgJoinSchema = createScreenSchema({
    type: z.literal('org_join'),
    organization: z.string(),
    invite: z.string(),
});

export const NavigationOrgSettingsPlansTabSchema = z
    .object({
        type: z.literal('plans'),
        interval: z.enum(['monthly', 'yearly']).optional(),
    })
    .strict();

export const NavigationOrgSettingsAgentsTabSchema = z
    .object({
        type: z.literal('agents'),
        tab: z.literal('settings').optional(),
    })
    .strict();

const NavigationIntegrationInstallationSchema = z
    .object({
        step: z.enum(['flow', 'configure']),
        context: z
            .object({
                type: z.enum(['site', 'space']),
                id: z.string(),
            })
            .strict()
            .optional(),
    })
    .strict();

/**
 * Organization settings tabs rendered inside the regular settings screen.
 */
export const NavigationOrgSettingsMainTabSchema = z.union([
    z
        .object({
            type: z.literal('profile'),
        })
        .strict(),
    z
        .object({
            type: z.literal('members'),
            memberId: z.string().optional(),
            detailsTab: z.string().optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal('teams'),
            teamId: z.string().optional(),
            detailsTab: z.string().optional(),
        })
        .strict(),
    NavigationOrgSettingsPlansTabSchema,
    z
        .object({
            type: z.literal('sso'),
        })
        .strict(),
    z
        .object({
            type: z.literal('invites'),
        })
        .strict(),
    z
        .object({
            type: z.literal('rules'),
        })
        .strict(),
    NavigationOrgSettingsAgentsTabSchema,
    z
        .object({
            type: z.literal('integrations'),
            search: z.string().optional(),
            category: z.enum(api.IntegrationCategory).optional(),
            provider: z.enum(NavigationIntegrationsProvider).optional(),
            integration: z.string().optional(),
            tab: z.enum(['about', 'installations']).optional(),
            installation: NavigationIntegrationInstallationSchema.optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal('openapi'),
            spec: z.string().optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal('translations'),
            view: z.enum(['overview', 'glossary']).optional(),
        })
        .strict(),
    z
        .object({
            type: z.literal('audit-logs'),
            eventId: z.string().optional(),
        })
        .strict(),
]);

export const NavigationScreenOrgSettingsSchema = createScreenSchema({
    type: z.literal('org_settings'),
    organization: z.string(),
    tab: NavigationOrgSettingsMainTabSchema,
});

/** Legacy organization-agnostic integrations URLs that redirect to organization settings. */
export const NavigationScreenLegacyIntegrationsSchema = createScreenSchema({
    type: z.literal('integrations_legacy'),
    organization: z.string().optional(),
    search: z.string().optional(),
    category: z.enum(api.IntegrationCategory).optional(),
    provider: z.enum(NavigationIntegrationsProvider).optional(),
    integration: z.string().optional(),
    tab: z.enum(['about', 'installations']).optional(),
    installation: NavigationIntegrationInstallationSchema.optional(),
});

export const NavigationAccountDeveloperTabSchema = z.enum([
    'api',
    'mcp',
    'cli',
    'client-library',
    'api-access-tokens',
]);

export const NavigationScreenAccountSettingsSchema = createScreenSchema({
    type: z.literal('account'),
    tab: z.enum(['profile', 'developer', 'organizations', 'notification']),
    developerTab: NavigationAccountDeveloperTabSchema.optional(),
});

export const NavigationScreenTrashStateSchema = createScreenSchema({
    type: z.literal('trash'),
    organization: z.string(),
});

export const NavigationScreenOrganizationContentStateSchema = createScreenSchema({
    type: z.literal('org_content'),
    organization: z.string(),
});

export const NavigationScreenStyleguidesStateSchema = createScreenSchema({
    type: z.literal('styleguides'),
    organization: z.string(),
});

export const NavigationScreenChangeRequestsStateSchema = createScreenSchema({
    type: z.literal('change_requests'),
    organization: z.string(),
    filters: NavigationScreenChangeRequestsFiltersSchema.optional(),
    site: z.string().optional(),
});

export const NavigationScreenTriageStateSchema = createScreenSchema({
    type: z.literal('triage'),
    organization: z.string(),
    site: z.string(),
    view: z.union([z.literal('settings'), z.literal('scans')]).optional(),
    finding: z.string().optional(),
    filters: NavigationScreenSiteFindingsFiltersSchema.optional(),
    scan: z.string().optional(),
    scanFilters: NavigationScreenSiteScansFiltersSchema.optional(),
});

export const NavigationScreenContentGapsStateSchema = createScreenSchema({
    type: z.literal('content_gaps'),
    organization: z.string(),
    site: z.string(),
    view: z.literal('settings').optional(),
    finding: z.string().optional(),
    filters: NavigationScreenSiteFindingsFiltersSchema.optional(),
    orderBy: z.enum(api.SiteFindingOrderBy).optional(),
});

export const NavigationScreenLinkAuditStateSchema = createScreenSchema({
    type: z.literal('link_audit'),
    organization: z.string(),
    site: z.string(),
    /**
     * Analysis shows the broken URLs analytics report, settings the broken links settings;
     * undefined shows the suggestions.
     */
    view: z.union([z.literal('analysis'), z.literal('settings')]).optional(),
    finding: z.string().optional(),
    filters: NavigationScreenSiteFindingsFiltersSchema.optional(),
    orderBy: z.enum(api.SiteFindingOrderBy).optional(),
    /** Part of a URL the findings list is narrowed to. */
    url: z.string().optional(),
    /** Filters of the analysis view's report, apart from `filters` which scopes the findings list. */
    analyticsFilters: NavigationScreenAnalyticsFiltersSchema.optional(),
});

export const NavigationScreenAgentsStateSchema = createScreenSchema({
    type: z.literal('agents'),
    organization: z.string(),
});

export const NavigationScreenAgentStateSchema = createScreenSchema({
    type: z.literal('agent'),
    organization: z.string(),
    site: z.string(),
    /** If undefined the screen picks the conversation: the latest one, or a new one. */
    conversation: z.string().optional(),
    /** The site's Git Sync setup, opened over the conversation. */
    sideSheet: NavigationGitSyncSideSheetSchema.optional(),
});

export const NavigationScreenStyleguideNewStateSchema = createScreenSchema({
    type: z.literal('styleguide_new'),
    organization: z.string(),
    site: z.string(),
});

export const NavigationScreenStateSchema = z.union([
    NavigationScreenRootSchema,
    NavigationScreenJoinSchema,
    NavigationScreenErrorNotFoundSchema,
    NavigationScreenHomeStateSchema,
    NavigationScreenOAuthAuthorizeSchema,
    NavigationScreenUserStateSchema,
    NavigationScreenAccountSettingsSchema,
    NavigationScreenOrgInviteSchema,
    NavigationScreenOrgWithBaseNameSchema,
    NavigationScreenOrgJoinSchema,
    NavigationScreenOrgSettingsSchema, // problematic
    NavigationScreenLegacyIntegrationsSchema,
    NavigationScreenWelcomeSchema,
    NavigationScreenSpaceStateSchema,
    NavigationScreenCollectionStateSchema,
    NavigationScreenTrashStateSchema,
    NavigationScreenSitePreviewAuthSchema,
    NavigationScreenSiteInviteSchema,
    NavigationScreenSiteStateSchema,
    NavigationScreenSiteStructureStateSchema,
    NavigationScreenSitesListStateSchema,
    NavigationScreenAgentStateSchema,
    NavigationScreenAgentsStateSchema,
    NavigationScreenChangeRequestsStateSchema,
    NavigationScreenTriageStateSchema,
    NavigationScreenContentGapsStateSchema,
    NavigationScreenLinkAuditStateSchema,
    NavigationScreenOrganizationContentStateSchema,
    NavigationScreenStyleguidesStateSchema,
    NavigationScreenStyleguideNewStateSchema,
]);

/**
 * Parses an unknown input into a NavigationScreenState if it matches the schema, or returns null if it doesn't.
 */
export function parseNavigationScreenState(
    input: unknown
): z.infer<typeof NavigationScreenStateSchema> | null {
    const result = NavigationScreenStateSchema.safeParse(input);
    return result.success ? result.data : null;
}
